import { useState, useEffect, useRef, useCallback } from 'react';

const API = () => process.env.REACT_APP_SERVICE_ENDPOINT_NE;

function tvFetch(path) {
    return fetch(`${API()}/${path}`, { headers: { Accept: 'application/json' } });
}

/**
 * Fetches and polls all REST data for the TV view.
 * All Televisor endpoints are public — no auth header needed.
 *
 * Returns:
 *   infoInicial       — full GetInfoInicial response (or null while loading)
 *   indicadores       — latest IndicadoresTurno response
 *   historial         — latest HistorialTurno array
 *   llamadasActivas   — llamadas from GetInfoInicial (updated on each full reload)
 *   conteoHistorico   — piezas de corridas terminadas en el turno
 *   conteoCorridaActual — snapshot de piezas corrida en curso (actualizado por SignalR)
 *   setConteoCorridaActual — setter para que useTelevisorHub actualice el conteo
 *   loading           — true mientras se carga GetInfoInicial por primera vez
 */
export default function useTelevisorData(linea) {
    const [infoInicial, setInfoInicial]               = useState(null);
    const [indicadores, setIndicadores]               = useState(null);
    const [historial, setHistorial]                   = useState([]);
    const [llamadasActivas, setLlamadasActivas]       = useState([]);
    const [conteoHistorico, setConteoHistorico]       = useState(0);
    const [conteoCorridaActual, setConteoCorridaActual] = useState(0);
    const [loading, setLoading]                       = useState(true);

    const inicioTurnoRef = useRef(null);

    const loadInfoInicial = useCallback(async () => {
        if (!linea) return;
        try {
            const res = await tvFetch(`Televisor/GetInfoInicial?Linea=${linea}`);
            if (!res.ok) return;
            const data = await res.json();

            const newInicio = data.inicioTurno ?? null;
            const turnoChanged = inicioTurnoRef.current !== null && newInicio !== inicioTurnoRef.current;

            inicioTurnoRef.current = newInicio;
            setInfoInicial(data);
            setLlamadasActivas(Array.isArray(data.llamadasActivas) ? data.llamadasActivas : []);
            setConteoHistorico(data.conteoHistorico ?? 0);

            // Solo resetear conteoCorridaActual al montar o cambio de turno
            if (turnoChanged || inicioTurnoRef.current === newInicio) {
                setConteoCorridaActual(data.conteoCorridaActual ?? 0);
            }
        } catch (_) {}
    }, [linea]);

    const fetchIndicadores = useCallback(async () => {
        if (!linea) return;
        try {
            const res = await tvFetch(`Televisor/IndicadoresTurno?Linea=${linea}`);
            if (res.ok) setIndicadores(await res.json());
        } catch (_) {}
    }, [linea]);

    const fetchHistorial = useCallback(async () => {
        if (!linea) return;
        try {
            const res = await tvFetch(`Televisor/HistorialTurno?Linea=${linea}`);
            if (res.ok) setHistorial(await res.json());
        } catch (_) {}
    }, [linea]);

    // Polling de GetInfoInicial (1 min): siempre actualiza corridaActual, llamadas e historial.
    // Solo resetea conteoCorridaActual si el turno cambió (para no pisar actualizaciones de SignalR).
    const checkTurno = useCallback(async () => {
        if (!linea) return;
        try {
            const res = await tvFetch(`Televisor/GetInfoInicial?Linea=${linea}`);
            if (!res.ok) return;
            const data = await res.json();
            const newInicio = data.inicioTurno ?? null;
            const turnoChanged = newInicio !== inicioTurnoRef.current;

            inicioTurnoRef.current = newInicio;
            setInfoInicial(data);
            setLlamadasActivas(Array.isArray(data.llamadasActivas) ? data.llamadasActivas : []);
            setConteoHistorico(data.conteoHistorico ?? 0);

            if (turnoChanged) {
                setConteoCorridaActual(data.conteoCorridaActual ?? 0);
            }
        } catch (_) {}
    }, [linea]);

    // Carga inicial
    useEffect(() => {
        if (!linea) return;
        setLoading(true);
        Promise.all([loadInfoInicial(), fetchIndicadores(), fetchHistorial()])
            .finally(() => setLoading(false));
    }, [linea, loadInfoInicial, fetchIndicadores, fetchHistorial]);

    // Polling IndicadoresTurno — cada 5 min
    useEffect(() => {
        if (!linea) return;
        const iv = setInterval(fetchIndicadores, 5 * 60 * 1000);
        return () => clearInterval(iv);
    }, [linea, fetchIndicadores]);

    // Polling HistorialTurno — cada 2 min
    useEffect(() => {
        if (!linea) return;
        const iv = setInterval(fetchHistorial, 2 * 60 * 1000);
        return () => clearInterval(iv);
    }, [linea, fetchHistorial]);

    // Detección cambio de turno — cada 1 min
    useEffect(() => {
        if (!linea) return;
        const iv = setInterval(checkTurno, 60 * 1000);
        return () => clearInterval(iv);
    }, [linea, checkTurno]);

    return {
        infoInicial,
        indicadores,
        historial,
        llamadasActivas,
        conteoHistorico,
        conteoCorridaActual,
        setConteoCorridaActual,
        loading,
    };
}
